// client/src/pages/superadmin/Profile.tsx

import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Avatar, 
  Upload, 
  Row, 
  Col, 
  Divider, 
  message,
  Typography,
  Tabs,
  Switch,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  SaveOutlined,
  SecurityScanOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SuperAdminProfile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [form] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Initialiser les valeurs du formulaire
  React.useEffect(() => {
    if (user) {
      form.setFieldsValue({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone || '',
      });
    }
  }, [user, form]);

  const handleProfileUpdate = async (values: any) => {
    setLoading(true);
    try {
      await updateProfile(values);
      setIsEditing(false);
      message.success('Profil mis à jour avec succès');
    } catch (error) {
      message.error('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values: any) => {
    setLoading(true);
    try {
      // API call to change password
      console.log('Password change:', values);
      message.success('Mot de passe mis à jour avec succès');
      securityForm.resetFields();
    } catch (error) {
      message.error('Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSettings = async (values: any) => {
    setLoading(true);
    try {
      // API call to update notification settings
      console.log('Notification settings:', values);
      message.success('Paramètres de notification mis à jour');
    } catch (error) {
      message.error('Erreur lors de la mise à jour des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'avatar',
    listType: 'picture-card' as const,
    className: 'avatar-uploader',
    showUploadList: false,
    beforeUpload: (file: File) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('Vous ne pouvez télécharger que des fichiers JPG/PNG!');
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('L\'image doit faire moins de 2MB!');
      }
      return isJpgOrPng && isLt2M;
    },
    onChange: (info: any) => {
      if (info.file.status === 'done') {
        message.success('Avatar mis à jour avec succès');
      }
    },
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Mon Profil</Title>
        <Text type="secondary">
          Gérez vos informations personnelles et paramètres de compte
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          {/* Carte profil */}
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Upload {...uploadProps}>
                <Avatar 
                  size={120} 
                  icon={<UserOutlined />}
                  style={{ 
                    cursor: 'pointer',
                    marginBottom: '16px'
                  }}
                />
              </Upload>
              <div>
                <Title level={4} style={{ margin: '16px 0 8px 0' }}>
                  {user?.first_name} {user?.last_name}
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                  Super Administrateur
                </Text>
                <Text type="secondary" style={{ display: 'block' }}>
                  {user?.email}
                </Text>
              </div>
              
              <Divider />
              
              <div style={{ textAlign: 'left' }}>
                <div style={{ marginBottom: '12px' }}>
                  <Text strong>Dernière connexion</Text>
                  <br />
                  <Text type="secondary">Aujourd'hui à 14:32</Text>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <Text strong>Membre depuis</Text>
                  <br />
                  <Text type="secondary">Janvier 2025</Text>
                </div>
                <div>
                  <Text strong>Statut</Text>
                  <br />
                  <Text style={{ color: '#52c41a' }}>● Actif</Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card>
            <Tabs defaultActiveKey="profile">
              <TabPane tab={<span><UserOutlined />Informations</span>} key="profile">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <Title level={4} style={{ margin: 0 }}>Informations personnelles</Title>
                  <Button
                    type={isEditing ? "default" : "primary"}
                    icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
                    onClick={() => {
                      if (isEditing) {
                        form.submit();
                      } else {
                        setIsEditing(true);
                      }
                    }}
                  >
                    {isEditing ? 'Sauvegarder' : 'Modifier'}
                  </Button>
                </div>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleProfileUpdate}
                  disabled={!isEditing}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Prénom"
                        name="first_name"
                        rules={[{ required: true, message: 'Prénom requis' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Nom"
                        name="last_name"
                        rules={[{ required: true, message: 'Nom requis' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Email requis' },
                      { type: 'email', message: 'Email invalide' }
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Téléphone"
                    name="phone"
                  >
                    <Input />
                  </Form.Item>
                </Form>
              </TabPane>

              <TabPane tab={<span><SecurityScanOutlined />Sécurité</span>} key="security">
                <Title level={4}>Changer le mot de passe</Title>
                <Form
                  form={securityForm}
                  layout="vertical"
                  onFinish={handlePasswordChange}
                >
                  <Form.Item
                    label="Mot de passe actuel"
                    name="currentPassword"
                    rules={[{ required: true, message: 'Mot de passe actuel requis' }]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <Form.Item
                    label="Nouveau mot de passe"
                    name="newPassword"
                    rules={[
                      { required: true, message: 'Nouveau mot de passe requis' },
                      { min: 8, message: 'Au moins 8 caractères' }
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <Form.Item
                    label="Confirmer le nouveau mot de passe"
                    name="confirmPassword"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: 'Confirmation requise' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Changer le mot de passe
                    </Button>
                  </Form.Item>
                </Form>
              </TabPane>

              <TabPane tab={<span><NotificationOutlined />Notifications</span>} key="notifications">
                <Title level={4}>Paramètres de notification</Title>
                <Form
                  form={notificationForm}
                  layout="vertical"
                  onFinish={handleNotificationSettings}
                  initialValues={{
                    emailNotifications: true,
                    systemAlerts: true,
                    weeklyReports: false,
                    marketingEmails: false,
                  }}
                >
                  <Form.Item
                    label="Notifications par email"
                    name="emailNotifications"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Alertes système"
                    name="systemAlerts"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Rapports hebdomadaires"
                    name="weeklyReports"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    label="Emails marketing"
                    name="marketingEmails"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Sauvegarder les paramètres
                    </Button>
                  </Form.Item>
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminProfile;